import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { deleteGroup, readAccessList } from "../../../api/access/accessApi";
import { useLoad } from "../../../shared/loading/useLoad";
import { appPaths } from "../../../shared/routing/appPaths";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import ConfirmAction from "../../../ui/overlays/ConfirmAction";
import { useAccessChanges, GroupScreen, groupNamer } from "../../access";

/**
 * Administration › Groups › a group (redesign R3-3 `admin-group`): its installation
 * permissions, the project permissions it brings to every project that includes it, and its
 * members. Putting someone into Administrators makes them an administrator.
 */
export default function InstallationGroupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupId = "" } = useParams();
  const load = useCallback((signal: AbortSignal) => readAccessList(null, signal), []);
  const { state, reload, refresh } = useLoad(load);
  const changes = useAccessChanges(refresh, state);
  const [deleting, setDeleting] = useState(false);

  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />;
  const list = state.value;
  const namer = groupNamer(t, list);
  const group = list.groups.find((listed) => listed.groupId === groupId);
  const back = appPaths.administrationGroups();
  return (
    <>
      <GroupScreen
        list={list}
        groupId={groupId}
        projectName={null}
        namer={namer}
        changes={changes}
        back={{ path: back, label: t("administration.pages.groups") }}
        removal={group !== undefined && group.manageable && group.system === null ? { label: t("access.delete"), run: () => { setDeleting(true); } } : null}
      />
      {changes.feedback}
      {deleting && group !== undefined ? (
        <ConfirmAction title={t("access.deleteTitle", { group: namer.name(group.groupId) })} confirmLabel={t("access.delete")} tone="danger"
          onConfirm={async () => { await deleteGroup(group.groupId); void navigate(back); }} onClose={() => { setDeleting(false); }}>
          <p>{t("access.deleteInstallationExplained")}</p>
        </ConfirmAction>
      ) : null}
    </>
  );
}
